<template>
  <div class="image-container">
    <!-- Zoom Controls -->
    <!-- <div class="zoom-controls" >
      <button @click="zoomIn">➕</button>
      <button @click="zoomOut">➖</button>
      <button @click="resetZoom">🔄</button>
    </div> -->

    <div class="zoom-controls-floating">
      <button class="zoom-btn" @click="zoomIn" title="Zoom In">
        <ZoomIn size="18" />
      </button>
      <button class="zoom-btn" @click="zoomOut" title="Zoom Out">
        <ZoomOut size="18" />
      </button>
      <button class="zoom-btn" @click="resetZoom" title="Reset">
        <RotateCcw size="18" />
      </button>
    </div>

    <!-- Image Wrapper with Drag/Pan -->
    <div class="image-wrapper" ref="imageWrapper" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag">
      <!-- NEW: Apply transform here -->
      <div class="image-transform-wrapper" :style="transformStyle">
        <img ref="imageRef" :src="imageSrc" alt="Highlighted Image" class="image" @load="setImageSize" />
        <div v-for="(box, index) in textData" :key="index" class="bounding-box" :style="getBoxStyle(box.BoundingBox)"></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineProps, computed } from 'vue'
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-vue-next'

// Props
const props = defineProps({
  textData: Array, // Bounding boxes array
  imageSrc: String, // Image URL
  isExpanded: Boolean,
})

// Refs
const imageRef = ref(null)
const imageWrapper = ref(null)
const imageWidth = ref(0)
const imageHeight = ref(0)
const zoomLevel = ref(1) // Default zoom level

// Dragging State
const isDragging = ref(false)
const dragStartX = ref(0)
const dragStartY = ref(0)
const offsetX = ref(0)
const offsetY = ref(0)

// Function to set actual image dimensions
const setImageSize = () => {
  if (imageRef.value) {
    imageWidth.value = imageRef.value.naturalWidth
    imageHeight.value = imageRef.value.naturalHeight
  }
}

// Function to calculate bounding box styles relative to the displayed image
const getBoxStyle = (boundingBox) => {
  if (!imageRef.value) return {}

  const imgWidth = imageRef.value.clientWidth
  const imgHeight = imageRef.value.clientHeight

  const leftPx = boundingBox.left * imgWidth - 8
  const topPx = boundingBox.top * imgHeight - 8
  const widthPx = boundingBox.width * imgWidth + 16
  const heightPx = boundingBox.height * imgHeight + 16

  return {
    position: 'absolute',
    left: `${leftPx}px`,
    top: `${topPx}px`,
    width: `${widthPx}px`,
    height: `${heightPx}px`,
    border: '1px solid rgb(56, 142, 60)',
    transform: `scale(${zoomLevel.value})`,
    transformOrigin: 'top left',
    backgroundColor: '#40ff0066',
  }
}

// Watch for changes in textData and update bounding boxes
watch(() => props.textData, setImageSize, { deep: true })

onMounted(() => {
  setImageSize()
  window.addEventListener('resize', setImageSize)
})

// Zoom functions
const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value + 0.5, 5) // Maximum 5x zoom
}
const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value - 0.5, 1) // Minimum 1x zoom
}
const resetZoom = () => {
  zoomLevel.value = 1
  offsetX.value = 0
  offsetY.value = 0
}

// Drag Functions
const startDrag = (event) => {
  if (zoomLevel.value > 1) {
    isDragging.value = true
    dragStartX.value = event.clientX - offsetX.value
    dragStartY.value = event.clientY - offsetY.value
  }
}

const onDrag = (event) => {
  if (isDragging.value) {
    offsetX.value = event.clientX - dragStartX.value
    offsetY.value = event.clientY - dragStartY.value
  }
}

const stopDrag = () => {
  isDragging.value = false
}

// Compute image style based on zoom and panning
// const imageStyle = computed(() => ({
//   transform: `scale(${zoomLevel.value}) translate(${offsetX.value}px, ${offsetY.value}px)`,
//   transformOrigin: 'top left',
//   transition: isDragging.value ? 'none' : 'transform 0.3s ease-in-out',
//   cursor: zoomLevel.value > 1 ? 'grab' : 'default',
// }))

const transformStyle = computed(() => ({
  transform: `scale(${zoomLevel.value}) translate(${offsetX.value}px, ${offsetY.value}px)`,
  transformOrigin: 'top left',
  transition: isDragging.value ? 'none' : 'transform 0.3s ease-in-out',
}))
</script>

<style scoped>
.zoom-controls-floating {
  position: absolute;
  bottom: 2vh;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 24px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
  display: flex;
  gap: 10px;
  padding: 6px 10px;
  z-index: 10;
}

.zoom-btn {
  background: none;
  border: none;
  font-size: 27px;
  padding: 2px 12px;
  cursor: pointer;
  color: #327ebf;
  border-radius: 4px;
}

.zoom-btn:hover {
  background-color: #eaf5fd;
}

.image-transform-wrapper {
  position: relative;
  display: inline-block;
}

.image-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.zoom-controls button {
  margin-left: 10px;

  font-size: 16px;
  cursor: pointer;
  border: 1px solid black;
  border-radius: 5px;
  color: white;
}

/* Image Wrapper */
.image-wrapper {
  position: relative;
  overflow-y: auto;
  display: inline-block;
  width: 100%;
  height: 100%;
  text-align: center;
  cursor: grab;
  background: white;
}

/* Image */
.image {
  max-width: 100%;
  height: auto;
  /* display: block; */
  object-fit: contain !important;
}

/* Bounding Box */
.bounding-box {
  position: absolute;
}
</style>
